import { useAppTranslation } from '@hooks/useAppTranslation';
import { useEffect, useMemo, useState } from 'react';
import { getForm } from '@api/form';
import { FormType, ProjectType } from '@pages/types';
import { colors, Content, Pills, Space, Text, Title } from 'common-components';
import { CurrentUserRole } from '@definitions/states';

import ReviewersAudienceProject from './ReviewersAudienceProject';
import ExcludedReviewersAudienceProject from './ExcludedReviewersAudienceProject';
import ContributorsAudienceProject from './ContributorsAudienceProject';

import { formApiMapper } from '@/mappers';

type AudienceProjectPropsType = {
  project: ProjectType
};

type PillsValueType = 'contributors' | 'reviewers' | 'excluded_contributors';
type OptionType = { label: string; value: PillsValueType };

const AudienceProject = ({ project }: AudienceProjectPropsType) => {
  const { translate } = useAppTranslation();
  const [activeTab, setActiveTab] = useState<PillsValueType>('contributors');
  const [form, setForm] = useState<FormType>();

  const modifEnabled =
    CurrentUserRole[project.currentUserRole] === 'admin' ||
    CurrentUserRole[project.currentUserRole] === 'creator';

  useEffect(() => {
    getForm(project?.form?.idForm).then((formApi) => {
      setForm(formApiMapper(formApi));
    });
  }, [project?.form?.idForm]);

  const options: OptionType[] = useMemo(() => {
    // TODO "Clean up the file 'src/definitions/states.ts' to reuse keys and values properly"
    const isReviewers =
      (form?.quality === 40 || form?.quality === 20) &&
      form?.template?.template?.quality.indeterminateMode === 'manual';
    const contributors: OptionType = {
      label: translate('project.audience.pills.contributers'),
      value: 'contributors',
    };
    const reviewers: OptionType = {
      label: translate('project.audience.pills.reviewers'),
      value: 'reviewers',
    };
    const excludedContributors: OptionType = {
      label: translate('project.audience.pills.excludedContributers'),
      value: 'excluded_contributors',
    };

    if (isReviewers) {
      return [contributors, reviewers, excludedContributors];
    }
    return [contributors, excludedContributors];
  }, [form?.quality, form?.template?.template?.quality.indeterminateMode]);

  const activeForm = useMemo(() => {
    switch (activeTab) {
      case 'contributors':
        return <ContributorsAudienceProject project={project} timePerTaskForm={form?.timePerTask} />;
      case 'reviewers':
        return <ReviewersAudienceProject project={project} timePerTaskForm={form?.timePerTask} />;
      case 'excluded_contributors':
        return (
          <ExcludedReviewersAudienceProject
            blacklistedUsers={project?.audience?.blacklistedUsers}
            modifEnabled={modifEnabled}
          />
        );
      default:
        return <p>{translate('common.error')}</p>;
    }
  }, [activeTab, project, form]);

  return (
    <Content>
      <Space orientation="vertical" size={4} style={{ marginBottom: 16 }}>
        <Title preset="singlePageTitle">{translate('project.audience.title')}</Title>
        <Text color={colors.gray600} style={{ lineHeight: '20px' }}>
          {translate('project.audience.subtitle')}
        </Text>
      </Space>
      <Pills
        value={activeTab}
        onChange={(e) => setActiveTab(e.target.value)}
        options={options}
        style={{ marginBottom: 16 }}
      />
      {activeForm}
    </Content>
  );
};

export default AudienceProject;
